/**
 * Service Call Prevention System (SCPS)
 *
 * Proactively detects and prevents post-install issues by:
 * 1. Enforcing pre-install validation gates
 * 2. Computing service-call risk scores per job
 * 3. Surfacing risk in Maverick and dispatch views
 *
 * ADDITIVE — does not modify existing engines.
 */

import type { Job, Issue } from "./types";
import { deriveFinancialClearance } from "./financialClearance";
import { hasValidCloseoutSignoff } from "./closeoutSignoff";
// readiness import removed — this module never consumed deriveReadinessFromJob

// ── Risk Types ──

export type ServiceCallRiskLevel = "low" | "medium" | "high";

export interface ServiceCallRiskScore {
  level: ServiceCallRiskLevel;
  score: number; // 0-100, higher = more risk
  factors: RiskFactor[];
  summary: string;
}

export interface RiskFactor {
  label: string;
  weight: number; // points added to score
  present: boolean;
  detail: string;
}

// ── Pre-Install Gate ──

export interface PreInstallGateResult {
  canProceed: boolean;
  blockers: PreInstallBlocker[];
  passedChecks: string[];
}

export interface PreInstallBlocker {
  code: string;
  label: string;
  detail: string;
  severity: "hard" | "soft";
}

export function evaluatePreInstallGate(job: Job, issues: Issue[]): PreInstallGateResult {
  const blockers: PreInstallBlocker[] = [];
  const passed: string[] = [];
  const openIssues = issues.filter(i => i.jobId === job.id && (i.status === "Open" || i.status === "In Progress"));

  // 1. Financial clearance (deposit, financing, or 0-down)
  const preSchedClearance = deriveFinancialClearance(job, "pre_schedule");
  if (preSchedClearance.status === "blocked") {
    blockers.push({ code: "FINANCIAL", label: "Not financially cleared", detail: preSchedClearance.reason, severity: "hard" });
  } else if (preSchedClearance.status === "pending") {
    blockers.push({ code: "FINANCIAL", label: "Financial clearance pending", detail: preSchedClearance.reason, severity: "soft" });
  } else {
    passed.push("Financially cleared");
  }

  // 2. Materials ordered
  if (job.materialReadiness === "Not Ordered") {
    blockers.push({ code: "MAT_ORDER", label: "Materials not ordered", detail: "No materials have been ordered for this job", severity: "hard" });
  } else {
    passed.push("Materials ordered");
  }

  // 3. Materials received
  if (job.materialReadiness === "Not Ordered" || job.materialReadiness === "Ordered") {
    if (job.materialReadiness === "Ordered") {
      blockers.push({ code: "MAT_RECV", label: "Materials not received", detail: "Materials ordered but not yet delivered", severity: "hard" });
    }
  } else {
    passed.push("Materials received");
  }

  // 4. Materials staged
  const stagedStatuses: string[] = ["Staged for Job", "Loaded for Crew"];
  if (!stagedStatuses.includes(job.materialReadiness ?? "")) {
    if (job.materialReadiness === "Fully Received" || job.materialReadiness === "Partially Received") {
      blockers.push({ code: "MAT_STAGE", label: "Materials not staged", detail: "Materials received but not staged for install", severity: "soft" });
    }
  } else {
    passed.push("Materials staged");
  }

  // 5. Materials loaded
  if (job.materialReadiness !== "Loaded for Crew") {
    if (job.materialReadiness === "Staged for Job") {
      blockers.push({ code: "MAT_LOAD", label: "Materials not loaded", detail: "Materials staged but not loaded onto truck", severity: "soft" });
    }
  } else {
    passed.push("Materials loaded for crew");
  }

  // 6. Build review approved
  if (job.currentStage === "Sale Closed" || job.currentStage === "Review Complete") {
    // Not yet through review
  } else {
    passed.push("Build review approved");
  }

  // 7. Schedule confirmed
  if (!job.scheduledInstallDate) {
    blockers.push({ code: "SCHEDULE", label: "Install not scheduled", detail: "No install date has been set", severity: "hard" });
  } else {
    passed.push("Schedule confirmed");
  }

  // 8. Missing items
  if (job.materialReadiness === "Missing Items") {
    blockers.push({ code: "MAT_MISSING", label: "Missing items detected", detail: "Some materials are missing or damaged", severity: "hard" });
  }

  // 9. Permit
  if (job.permitRequired && job.permitStatus !== "Approved" && job.permitStatus !== "Not Required") {
    blockers.push({ code: "PERMIT", label: "Permit not approved", detail: `Permit status: ${job.permitStatus || "Unknown"}`, severity: "hard" });
  } else {
    passed.push("Permit cleared");
  }

  // 10. Open critical issues
  const criticalIssues = openIssues.filter(i => i.severity === "Urgent");
  if (criticalIssues.length > 0) {
    blockers.push({ code: "ISSUES", label: `${criticalIssues.length} critical issue(s)`, detail: "Urgent issues must be resolved before install", severity: "hard" });
  }

  const hardBlockers = blockers.filter(b => b.severity === "hard");
  return {
    canProceed: hardBlockers.length === 0,
    blockers,
    passedChecks: passed,
  };
}

// ── Closeout Gate ──

export interface CloseoutGateResult {
  canClose: boolean;
  blockers: string[];
}

export function evaluateCloseoutGate(
  job: Job,
  issues: Issue[],
  options?: { skipQaPassedCheck?: boolean },
): CloseoutGateResult {
  const blockers: string[] = [];
  const openIssues = issues.filter(i => i.jobId === job.id && (i.status === "Open" || i.status === "In Progress"));

  // QA
  // Accept either explicit closeout status progression OR an actual submitted/passed QA record.
  // This keeps closeout gate behavior aligned with DB-backed QA submission flow.
  const qaSatisfied =
    job.closeoutStatus === "QA Passed" ||
    job.closeoutStatus === "Ready for Closeout" ||
    job.closeoutStatus === "Closed" ||
    job.qaSubmitted === true ||
    job.qaStatus === "Submitted" ||
    job.qaStatus === "Passed";
  if (!options?.skipQaPassedCheck && !qaSatisfied) {
    blockers.push("QA review not passed");
  }

  // Issues
  if (openIssues.length > 0) {
    blockers.push(`${openIssues.length} unresolved issue(s)`);
  }

  // Payment
  const postClearance = deriveFinancialClearance(job, "post_install");
  if (postClearance.status === "blocked") {
    blockers.push("Final payment not collected");
  }

  if (!hasValidCloseoutSignoff(job)) {
    blockers.push("Homeowner closeout sign-off is missing");
  }

  return { canClose: blockers.length === 0, blockers };
}

// ── Service Call Risk Score ──

export function computeServiceCallRisk(job: Job, issues: Issue[]): ServiceCallRiskScore {
  const openIssues = issues.filter(i => i.jobId === job.id && (i.status === "Open" || i.status === "In Progress"));

  const factors: RiskFactor[] = [
    {
      label: "Missing materials",
      weight: 25,
      present: job.materialReadiness === "Missing Items" || job.materialReadiness === "Field Purchase Required",
      detail: "Missing or field-purchased materials increase install errors",
    },
    {
      label: "Delayed ordering",
      weight: 15,
      present: job.materialReadiness === "Not Ordered" && (job.currentStage === "Scheduled Confirmed" || job.currentStage === "Ready for Dispatch"),
      detail: "Job is scheduled but materials have not been ordered",
    },
    {
      label: "Rushed install timeline",
      weight: 20,
      present: isRushedTimeline(job),
      detail: "Install date is within 48 hours of material receipt",
    },
    {
      label: "Skipped QA steps",
      weight: 20,
      present: job.closeoutStatus === "Install Complete" && !job.qaSubmitted,
      detail: "QA review has not been performed",
    },
    {
      label: "Unresolved issues",
      weight: 15,
      present: openIssues.length > 0,
      detail: `${openIssues.length} open issue(s) on this job`,
    },
    {
      label: "Permit pending during install",
      weight: 10,
      present: !!(job.permitRequired && job.permitStatus !== "Approved" && job.permitStatus !== "Not Required" && (job.currentStage === "In Progress" || job.currentStage === "Scheduled Confirmed")),
      detail: "Install proceeding without approved permit",
    },
    {
      label: "No crew assignment",
      weight: 10,
      present: job.assignmentType === "Unassigned" && (job.currentStage === "Scheduled Confirmed" || job.currentStage === "Ready for Dispatch"),
      detail: "No crew assigned for upcoming install",
    },
    {
      label: "Financial hold",
      weight: 10,
      present: deriveFinancialClearance(job, "pre_install").status === "blocked",
      detail: "Financial obligations not met before install",
    },
  ];

  const score = factors.filter(f => f.present).reduce((sum, f) => sum + f.weight, 0);
  const level: ServiceCallRiskLevel = score >= 40 ? "high" : score >= 20 ? "medium" : "low";

  const activeFactors = factors.filter(f => f.present);
  const summary = activeFactors.length === 0
    ? "Low risk — all conditions met"
    : `${level === "high" ? "High" : "Medium"} risk — ${activeFactors.map(f => f.label.toLowerCase()).join(", ")}`;

  return { level, score, factors, summary };
}

function isRushedTimeline(job: Job): boolean {
  if (!job.scheduledInstallDate) return false;
  const installDate = new Date(job.scheduledInstallDate);
  const now = new Date();
  const hoursUntilInstall = (installDate.getTime() - now.getTime()) / (1000 * 60 * 60);
  return hoursUntilInstall > 0 && hoursUntilInstall < 48 && (
    job.materialReadiness === "Ordered" ||
    job.materialReadiness === "Partially Received" ||
    job.materialReadiness === "Fully Received"
  );
}

// ── Install Day Checklist ──

export interface InstallDayCheck {
  id: string;
  label: string;
  required: boolean;
}

export const INSTALL_DAY_CHECKS: InstallDayCheck[] = [
  { id: "materials_present", label: "All materials present on site", required: true },
  { id: "items_verified", label: "Correct items verified against work order", required: true },
  { id: "scope_matches", label: "Scope matches job details", required: true },
  { id: "no_defects", label: "No visible defects or issues", required: true },
];

export function validateInstallDayChecklist(checks: Record<string, boolean>): { valid: boolean; missing: string[] } {
  const missing: string[] = [];
  for (const check of INSTALL_DAY_CHECKS) {
    if (check.required && !checks[check.id]) {
      missing.push(check.label);
    }
  }
  return { valid: missing.length === 0, missing };
}

// ── Risk Level Helpers ──

export const RISK_CONFIG: Record<ServiceCallRiskLevel, { label: string; color: string; bg: string; border: string }> = {
  low: { label: "Low Risk", color: "text-success", bg: "bg-success/10", border: "border-success/20" },
  medium: { label: "Medium Risk", color: "text-warning", bg: "bg-warning/10", border: "border-warning/20" },
  high: { label: "High Risk", color: "text-destructive", bg: "bg-destructive/10", border: "border-destructive/20" },
};
